
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { campo } from './ui/campo';
import { useIdioma } from '../i18n/idioma';
import toast from 'react-hot-toast';
import { useCRM } from '../contexts/CRMContext';
import { useSettings } from '../contexts/SettingsContext';
import { Client, Lead, LeadStatus } from '../types';
import { TRADES } from '../data/trades';
import { CATEGORIES_BY_TRADE } from '../data/categories/registry';
import { X, Search, User, Home, ArrowRight, ArrowLeft, Check, Phone, Sparkles, FilePlus } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import { isDuplicateContactError } from '../services/clientValidation';
import { Button } from './ui/Button';
import { SearchInput } from './ui/SearchInput';
import { SegmentedControl } from './ui/SegmentedControl';
import { EmptyState } from './ui/EmptyState';

export interface JobFlowFinishPayload {
  /** Vacío cuando el trabajo se arma para un prospecto: el cliente aún no
   *  existe y lo crea la conversión, cuando el trato se gane. */
  clientId: string;
  clientName: string;
  projectType: string;
  tradeId?: string;
  scopeCategoryId?: string;
  scopeLabel?: string;
  /** Prospecto de origen, si se eligió uno en vez de un cliente. */
  sourceLeadId?: string;
}

interface CreateEstimateModalProps {
  onClose: () => void;
  onFinish: (payload: JobFlowFinishPayload) => void;
  initialClientId?: string;
  /** Arranca directamente sobre este prospecto — desde el panel de Prospectos. */
  initialLeadId?: string;
  /** Shown in step 1 header — defaults to "Start a Job" */
  flowTitle?: string;
}

type WizardStep = 'client' | 'trade' | 'scope';

/** Para quién se arma el trabajo: un cliente que ya existe, un prospecto del
 *  tablero, o alguien nuevo que se da de alta aquí mismo. */
type PickerMode = 'existing' | 'lead' | 'new';

const STEP_ORDER: WizardStep[] = ['client', 'trade', 'scope'];

/** Prospectos que todavía pueden recibir una propuesta. Los perdidos no, y los
 *  ganados tampoco: esos ya son clientes y salen en la pestaña de al lado. */
const STAGES_COTIZABLES = new Set<LeadStatus>([
  LeadStatus.NEW,
  LeadStatus.CONTACTED,
  LeadStatus.SITE_VISIT,
  LeadStatus.ESTIMATE_NEEDED,
  LeadStatus.PROPOSAL_SENT,
]);

const CreateEstimateModal: React.FC<CreateEstimateModalProps> = ({
  onClose,
  onFinish,
  initialClientId,
  initialLeadId,
  flowTitle = 'Start a Job',
}) => {
    const { t } = useIdioma();
  const { clients, addClient, leads, updateLead } = useCRM();
  const { settings } = useSettings();

  const defaultTradeId = TRADES.find(
    t => t.id === settings.profile.industryType && t.enabled
  )?.id ?? null;

  const [step, setStep] = useState<WizardStep>('client');
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);
  /** Prospecto elegido. Cuando está puesto, `selectedClient.id` va vacío. */
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedTrade, setSelectedTrade] = useState<string | null>(defaultTradeId);
  const [selectedScope, setSelectedScope] = useState<{ id: string; label: string } | null>(null);

  const [clientSearch, setClientSearch] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [scopeSearch, setScopeSearch] = useState('');
  const [pickerMode, setPickerMode] = useState<PickerMode>('existing');

  /* La dirección se pide AQUÍ cuando el prospecto no la tiene. Un prospecto se
     apunta en mitad de una llamada y muchas veces sin dirección; una propuesta,
     en cambio, es un documento que dice dónde se hace el trabajo, y el cliente
     que salga de ella entra en las rutas. Este es el momento en que el usuario
     tiene el dato delante. */
  const [leadAddress, setLeadAddress] = useState('');
  const [savingLeadAddress, setSavingLeadAddress] = useState(false);

  const [newClientData, setNewClientData] = useState({
    name: '',
    phone: '',
    address: '',
    email: ''
  });

  const selectedLead = useMemo(
    () => leads.find(l => l.id === selectedLeadId) ?? null,
    [leads, selectedLeadId],
  );

  const stepIndex = STEP_ORDER.indexOf(step);

  const scopeOptions = useMemo(() => {
    if (!selectedTrade) return [];
    const categories = CATEGORIES_BY_TRADE[selectedTrade] ?? [];
    if (categories.length > 0) {
      return categories.map(c => ({ id: c.id, label: c.label, note: c.note, Icon: c.icon }));
    }
    return [{ id: 'general', label: t('estimados.crear.alcance_general'), note: undefined, Icon: undefined }];
  }, [selectedTrade]);

  // Every trade ships 15-17 scopes, so the list always outruns the modal and
  // half of it sits below the fold. The note is searched too: it carries the
  // words people actually think in ("aeration" lives in Lawn Care's note, not
  // in its label).
  const visibleScopeOptions = useMemo(() => {
    const q = scopeSearch.trim().toLowerCase();
    if (!q) return scopeOptions;
    return scopeOptions.filter(
      s => s.label.toLowerCase().includes(q) || (s.note?.toLowerCase().includes(q) ?? false)
    );
  }, [scopeOptions, scopeSearch]);

  useEffect(() => {
    if (initialClientId) {
      const found = clients.find(c => c.id === initialClientId);
      if (found) {
        setSelectedClient({ id: found.id, name: found.name });
        setStep('trade');
      }
    }
  }, [initialClientId, clients]);

  /* Entrada desde el panel del prospecto. Si le falta la dirección se queda en
     el paso 1 con el campo a la vista, en vez de saltar al oficio y arrastrar
     el hueco hasta el documento.

     Corre UNA sola vez (`yaPrecargado`) aunque `leads` cambie. Sin el cerrojo,
     guardar la dirección refresca la lista, el efecto vuelve a entrar y pisa lo
     que se esté tecleando o devuelve al paso 2 a quien acaba de pulsar Atrás. */
  const yaPrecargado = useRef(false);
  useEffect(() => {
    if (!initialLeadId || yaPrecargado.current) return;
    const found = leads.find(l => l.id === initialLeadId);
    if (!found) return;
    yaPrecargado.current = true;
    setPickerMode('lead');
    setSelectedLeadId(found.id);
    setSelectedClient({ id: '', name: found.name });
    setLeadAddress(found.address || '');
    if (found.address?.trim()) setStep('trade');
  }, [initialLeadId, leads]);

  /* Se entró con el prospecto ya decidido —desde su ficha—, así que el paso 1
     no vuelve a preguntar para quién es: ni pestañas de Cliente/Prospecto/Nuevo,
     ni buscador, ni el aviso de «no se crea cliente» (la propia ficha de la que
     se viene ya lo dice). Lo único que puede faltar es dónde es el trabajo. */
  const prospectoFijado = Boolean(initialLeadId) && selectedLeadId === initialLeadId && !!selectedLead;

  useEffect(() => {
    if (step === 'trade' && !selectedTrade && defaultTradeId) {
      setSelectedTrade(defaultTradeId);
    }
  }, [step, selectedTrade, defaultTradeId]);

  useEffect(() => {
    if (step === 'scope' && scopeOptions.length === 1 && !selectedScope) {
      setSelectedScope(scopeOptions[0]);
    }
  }, [step, scopeOptions, selectedScope]);

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.address.toLowerCase().includes(clientSearch.toLowerCase())
  ).slice(0, 5);

  // El teléfono entra en la búsqueda a propósito: en el tablero de prospectos
  // hay tocayos, y lo que distingue a uno de otro es el número.
  const filteredLeads = useMemo(() => {
    const q = leadSearch.trim().toLowerCase();
    return leads
      .filter(l => STAGES_COTIZABLES.has(l.status))
      .filter(l => !q
        || l.name.toLowerCase().includes(q)
        || l.address.toLowerCase().includes(q)
        || l.phone.toLowerCase().includes(q))
      .slice(0, 6);
  }, [leads, leadSearch]);

  const handleClientSelect = (client: Client) => {
    setSelectedLeadId(null);
    setSelectedClient({ id: client.id, name: client.name });
    setStep('trade');
  };

  const handleLeadSelect = (lead: Lead) => {
    setSelectedLeadId(lead.id);
    // Id vacío: el cliente no existe todavía y no debe existir hasta que el
    // trato se gane. Eso es justamente lo que evita llenar la lista de clientes
    // de gente que solo pidió un precio.
    setSelectedClient({ id: '', name: lead.name });
    setLeadAddress(lead.address || '');
    if (lead.address?.trim()) setStep('trade');
  };

  const handleLeadAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead || !leadAddress.trim()) return;
    // La dirección que ya estaba no se vuelve a guardar: desde la ficha del
    // prospecto este paso también sirve para confirmarla, y confirmar no es
    // motivo para escribir en la base de datos.
    if (leadAddress.trim() === (selectedLead.address || '').trim()) {
      setStep('trade');
      return;
    }
    setSavingLeadAddress(true);
    try {
      await updateLead(selectedLead.id, { address: leadAddress.trim() });
      setStep('trade');
    } catch (error) {
      console.error('Failed to save lead address', error);
      toast.error(t('estimados.crear.prospecto_error_direccion'));
    } finally {
      setSavingLeadAddress(false);
    }
  };

  const handleNewClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClientData.name || !newClientData.email.trim()) return;

    try {
      const newId = await addClient({
        name: newClientData.name,
        phone: newClientData.phone,
        email: newClientData.email,
        address: newClientData.address,
        preferredContact: 'Phone',
        propertyNotes: '',
        gateCode: '',
        tags: ['New']
      });

      setSelectedLeadId(null);
      setSelectedClient({ id: newId, name: newClientData.name });
      setStep('trade');
    } catch (error: any) {
      // Duplicate-phone/email guard from useCRMQueries — intentional validation,
      // not a bug. Show the real message (it names the existing record) instead
      // of the generic toast, and keep it out of the admin error logger.
      // Se mira el CÓDIGO del error, no su texto: el mensaje va traducido y el
      // `/already used by/` de antes no reconocía nada con la app en español.
      if (isDuplicateContactError(error)) {
        toast.error(error.message);
      } else {
        console.error('Failed to create client', error);
        toast.error(t('estimados.crear.error_crear_cliente'));
      }
    }
  };

  const handleTradeSelect = (tradeId: string) => {
    setSelectedTrade(tradeId);
    setSelectedScope(null);
    setScopeSearch('');
  };

  const goToScopeStep = () => {
    if (!selectedTrade) return;
    setSelectedScope(null);
    setScopeSearch('');
    setStep('scope');
  };

  /* Salida por el paso 2: el estimado nace SIN alcance.
   *
   * Es lo que separa este camino del normal. `scopeCategoryId`/`scopeLabel` van
   * vacíos a propósito, y de ahí cuelga todo lo demás: el constructor no
   * precarga ninguna plantilla, el panel de herramientas abre en Materiales en
   * vez de en Plantillas filtradas, y el nombre del proyecto sale genérico
   * —«General Project for …»— para que se teclee en la barra del constructor.
   *
   * El oficio SÍ viaja, y es el del taller (`settings.profile.industryType`),
   * no uno inventado: es el mismo que el paso 2 trae ya marcado, así que quien
   * salta se lleva exactamente lo que se habría llevado pulsando Siguiente sin
   * tocar nada. Sin él, la pestaña de Plantillas caería en el oficio por
   * defecto del código —jardinería— aunque el taller sea de techos. */
  const handleFinishBlank = () => {
    if (!selectedClient) return;
    const trade = defaultTradeId ? TRADES.find(t => t.id === defaultTradeId) : undefined;
    onFinish({
      clientId: selectedClient.id,
      clientName: selectedClient.name,
      projectType: trade?.label || 'General',
      tradeId: defaultTradeId ?? undefined,
      sourceLeadId: selectedLeadId || undefined,
    });
  };

  const handleFinish = () => {
    if (!selectedClient || !selectedTrade || !selectedScope) return;
    const trade = TRADES.find(t => t.id === selectedTrade);
    const projectType = trade?.label || 'General';
    onFinish({
      clientId: selectedClient.id,
      clientName: selectedClient.name,
      projectType,
      tradeId: selectedTrade,
      scopeCategoryId: selectedScope.id,
      scopeLabel: selectedScope.label,
      sourceLeadId: selectedLeadId || undefined,
    });
  };

  const enabledTrades = TRADES.filter(t => t.enabled);

  const inputClass = campo({ alto: 'auto' });
  const labelClass = 'label-ui block mb-1.5';

  return (
    /* `z-[250]`: la misma altura que sus hermanos del WorkflowController
       (ClientModal y el aviso de guardado). Con el `z-50` de antes se metía por
       DEBAJO de las fichas laterales —prospecto y cliente van en `z-[220]`— y
       de la cabecera clavada, así que al abrirlo desde un prospecto salía
       apagado detrás de la cortina del panel. El único que va más alto sigue
       siendo QuickPricingSetupModal (`z-[260]`), que se enseña encima de este. */
    <div className="fixed inset-0 bg-navy-950/50 z-[250] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-3xl shadow-s3 w-full sm:max-w-lg overflow-hidden h-[90dvh] sm:h-[650px] flex flex-col">
        <div className="px-4 py-4 flex items-center justify-between shrink-0 border-b border-acero-200">
          <div className="flex items-center gap-1.5 pl-2">
            {STEP_ORDER.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  stepIndex === i ? 'bg-brand-400' : stepIndex > i ? 'bg-navy-800' : 'bg-acero-200'
                }`} />
                {i < STEP_ORDER.length - 1 && (
                  <div className={`w-6 h-0.5 ${stepIndex > i ? 'bg-navy-300' : 'bg-acero-200'} transition-colors`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('estimados.crear.cerrar')}
            className="p-2 rounded-lg text-peligro hover:bg-peligro-bg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 sm:px-8 pb-8 pt-6 flex-1 flex flex-col min-h-0">
          {/* PASO 1, VENIDO DE LA FICHA DEL PROSPECTO — el destinatario ya está
              puesto y no se toca aquí. Queda una sola pregunta: la dirección. */}
          {step === 'client' && prospectoFijado && selectedLead && (
            <div className="animate-in fade-in flex flex-col h-full">
              <div className="mb-5">
                <p className="label-ui mb-1.5">{t('estimados.crear.paso1')}</p>
                <h3 className="text-[22px] leading-tight font-semibold tracking-tight text-navy-950 break-words">
                  {t('estimados.crear.prospecto_titulo_fijo', { nombre: selectedLead.name })}
                </h3>
                <p className="text-sm text-acero-500 mt-1">
                  {selectedLead.address?.trim()
                    ? t('estimados.crear.prospecto_sub_confirma')
                    : t('estimados.crear.prospecto_sub_falta')}
                </p>
              </div>

              <form onSubmit={handleLeadAddressSubmit} className="flex-1 flex flex-col min-h-0 space-y-4">
                <div className="rounded-xl border border-acero-200 bg-acero-50 px-4 py-3">
                  <p className="text-sm font-medium text-navy-950">{selectedLead.name}</p>
                  {selectedLead.phone && (
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-acero-500"><Phone size={10} />{selectedLead.phone}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>{t('estimados.crear.prospecto_falta_direccion')}</label>
                  <AddressAutocomplete
                    value={leadAddress}
                    onChange={setLeadAddress}
                    required
                    className={`${inputClass} pl-10`}
                    iconClassName="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400"
                    iconSize={16}
                    placeholder={t('estimados.crear.buscar_direccion')}
                  />
                  <p className="mt-1.5 text-xs text-acero-500">{t('estimados.crear.prospecto_direccion_nota')}</p>
                </div>
                {/* `mt-auto`: el botón se queda pegado al pie del panel, donde
                    están los de los pasos 2 y 3, en vez de bailar según lo que
                    ocupe el desplegable de direcciones. */}
                <div className="mt-auto pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={!leadAddress.trim() || savingLeadAddress}
                    rightIcon={<ArrowRight size={16} />}
                  >
                    {leadAddress.trim() === (selectedLead.address || '').trim()
                      ? t('estimados.crear.prospecto_continuar')
                      : t('estimados.crear.prospecto_guardar_seguir')}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {step === 'client' && !prospectoFijado && (
            <div className="animate-in fade-in flex flex-col h-full">
              <div className="mb-5">
                <p className="label-ui mb-1.5">{t('estimados.crear.paso1')}</p>
                <h3 className="text-[22px] leading-tight font-semibold tracking-tight text-navy-950">{flowTitle}</h3>
                <p className="text-sm text-acero-500 mt-1">{t('estimados.crear.para_quien')}</p>
              </div>

              <SegmentedControl<PickerMode>
                value={pickerMode}
                onChange={setPickerMode}
                options={[
                  { value: 'existing', label: t('estimados.crear.cliente_existente') },
                  { value: 'lead', label: t('estimados.crear.prospecto') },
                  { value: 'new', label: t('estimados.crear.cliente_nuevo') },
                ]}
                className="w-full mb-5 [&>button]:flex-1"
              />

              {pickerMode === 'lead' ? (
                <div className="flex-1 flex flex-col min-h-0 space-y-4">
                  {/* Lo que hace distinto a este camino, dicho una vez y en el
                      sitio donde se decide: no se crea ningún cliente. */}
                  <p className="shrink-0 flex items-start gap-2 text-xs leading-relaxed text-acero-500 bg-acero-50 border border-acero-200 rounded-xl px-3 py-2.5">
                    <Sparkles size={13} className="shrink-0 mt-0.5 text-brand-500" />
                    {t('estimados.crear.prospecto_explicacion')}
                  </p>

                  {selectedLead && !selectedLead.address?.trim() ? (
                    /* Falta la dirección. No se avanza sin ella: la propuesta la
                       imprime y el cliente que salga de aquí entra en las rutas. */
                    <form onSubmit={handleLeadAddressSubmit} className="flex-1 flex flex-col min-h-0 space-y-4">
                      <div className="rounded-xl border border-acero-200 bg-white px-4 py-3">
                        <p className="text-sm font-medium text-navy-950">{selectedLead.name}</p>
                        {selectedLead.phone && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs text-acero-500"><Phone size={10} />{selectedLead.phone}</p>
                        )}
                      </div>
                      <div>
                        <label className={labelClass}>{t('estimados.crear.prospecto_falta_direccion')}</label>
                        <AddressAutocomplete
                          value={leadAddress}
                          onChange={setLeadAddress}
                          required
                          className={`${inputClass} pl-10`}
                          iconClassName="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400"
                          iconSize={16}
                          placeholder={t('estimados.crear.buscar_direccion')}
                        />
                        <p className="mt-1.5 text-xs text-acero-500">{t('estimados.crear.prospecto_direccion_nota')}</p>
                      </div>
                      <div className="flex gap-3 pt-1">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => { setSelectedLeadId(null); setSelectedClient(null); setLeadAddress(''); }}
                          leftIcon={<ArrowLeft size={14} />}
                        >
                          {t('comun.atras')}
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          className="flex-1"
                          disabled={!leadAddress.trim() || savingLeadAddress}
                          rightIcon={<ArrowRight size={16} />}
                        >
                          {t('estimados.crear.prospecto_guardar_seguir')}
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <SearchInput
                        autoFocus
                        value={leadSearch}
                        onChange={setLeadSearch}
                        placeholder={t('estimados.crear.buscar_prospecto')}
                        className="w-full shrink-0"
                      />

                      <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-acero-100 border-t border-acero-200">
                        {filteredLeads.map(lead => (
                          <button
                            key={lead.id}
                            type="button"
                            onClick={() => handleLeadSelect(lead)}
                            className="w-full text-left px-2 py-3 hover:bg-acero-50 transition-colors group flex justify-between items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                          >
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-navy-950 truncate">{lead.name}</span>
                              <span className="mt-0.5 flex items-center gap-1.5 text-xs text-acero-500 truncate">
                                {lead.address
                                  ? <><Home size={10} className="shrink-0" />{lead.address}</>
                                  : <><Phone size={10} className="shrink-0" />{lead.phone || t('estimados.crear.prospecto_sin_datos')}</>}
                              </span>
                            </span>
                            <ArrowRight size={16} className="shrink-0 text-acero-500 group-hover:text-navy-700 transition-colors" />
                          </button>
                        ))}
                        {filteredLeads.length === 0 && (
                          <EmptyState
                            icon={<User size={18} />}
                            variant={leadSearch ? 'filtro' : 'vacio'}
                            title={leadSearch
                              ? t('estimados.crear.sin_prospectos_filtro_titulo')
                              : t('estimados.crear.sin_prospectos_titulo')}
                            description={leadSearch
                              ? t('estimados.crear.sin_prospectos_filtro_texto')
                              : t('estimados.crear.sin_prospectos_texto')}
                            className="!py-10"
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : pickerMode === 'existing' ? (
                <div className="flex-1 flex flex-col min-h-0 space-y-4">
                  <SearchInput
                    autoFocus
                    value={clientSearch}
                    onChange={setClientSearch}
                    placeholder={t('estimados.crear.buscar_cliente')}
                    className="w-full shrink-0"
                  />

                  <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar divide-y divide-acero-100 border-t border-acero-200">
                    {filteredClients.map(client => (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => handleClientSelect(client)}
                        className="w-full text-left px-2 py-3 hover:bg-acero-50 transition-colors group flex justify-between items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-navy-950 truncate">{client.name}</span>
                          <span className="mt-0.5 flex items-center gap-1 text-xs text-acero-500 truncate"><Home size={10} className="shrink-0" />{client.address}</span>
                        </span>
                        <ArrowRight size={16} className="shrink-0 text-acero-500 group-hover:text-navy-700 transition-colors" />
                      </button>
                    ))}
                    {filteredClients.length === 0 && clientSearch && (
                      <EmptyState
                        icon={<User size={18} />}
                        variant="filtro"
                        title={t('estimados.crear.sin_clientes_titulo')}
                        description={t('estimados.crear.sin_clientes_texto')}
                        action={
                          <Button variant="outline" size="sm" onClick={() => setPickerMode('new')}>
                            {t('estimados.crear.crear_cliente')}
                          </Button>
                        }
                        className="!py-10"
                      />
                    )}
                  </div>
                </div>
              ) : (
                <form onSubmit={handleNewClientSubmit} className="space-y-4 overflow-y-auto pr-1">
                  <div>
                    <label className={labelClass}>{t('estimados.crear.campo_nombre')}</label>
                    <input
                      required
                      className={inputClass}
                      value={newClientData.name}
                      onChange={e => setNewClientData({ ...newClientData, name: e.target.value })}
                      placeholder={t('estimados.crear.ejemplo_nombre')}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>{t('estimados.crear.campo_telefono')}</label>
                      {/* `type` e `inputMode` no son adorno: sin ellos el iPhone
                          abre el teclado de LETRAS para escribir un teléfono, y
                          hay que pulsar «123» en cada alta. El campo de correo
                          de al lado ya lo hacía bien, y `ClientModal` y
                          `LeadModal` también — este se quedó atrás. */}
                      <input
                        type="tel"
                        inputMode="tel"
                        required
                        className={inputClass}
                        value={newClientData.phone}
                        onChange={e => setNewClientData({ ...newClientData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>{t('estimados.crear.campo_email')}</label>
                      <input
                        type="email"
                        required
                        className={inputClass}
                        value={newClientData.email}
                        onChange={e => setNewClientData({ ...newClientData, email: e.target.value })}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>{t('estimados.crear.campo_direccion')}</label>
                    <AddressAutocomplete
                      value={newClientData.address}
                      onChange={(val) => setNewClientData(prev => ({ ...prev, address: val }))}
                      required
                      className={`${inputClass} pl-10`}
                      iconClassName="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400"
                      iconSize={16}
                      placeholder={t('estimados.crear.buscar_direccion')}
                    />
                  </div>

                  <div className="pt-4">
                    <Button type="submit" variant="primary" size="lg" className="w-full" rightIcon={<ArrowRight size={16} />}>
                      {t('estimados.crear.crear_y_seguir')}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {step === 'trade' && (
            <div className="animate-in fade-in flex flex-col h-full">
              <div className="mb-5">
                <p className="label-ui mb-1.5">{t('estimados.crear.paso2')}</p>
                <h3 className="text-[22px] leading-tight font-semibold tracking-tight text-navy-950">{t('estimados.crear.tipo_trabajo')}</h3>
                <p className="text-sm text-acero-500 mt-1">
                  {t('estimados.crear.que_oficio')} <span className="font-semibold text-navy-950">{selectedClient?.name}</span>?
                </p>
              </div>

              {/* La salida rápida va ARRIBA de la rejilla, no en el pie: la
                  rejilla de oficios se desplaza, y un botón dentro del área que
                  se desplaza queda debajo del doblez en cuanto hay más de seis
                  oficios — que es siempre. */}
              <button
                type="button"
                onClick={handleFinishBlank}
                className="shrink-0 mb-4 w-full flex items-center gap-3 rounded-xl border border-acero-200 bg-acero-50 px-4 py-3 text-left transition-colors duration-150 hover:border-acero-300 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-acero-200 bg-white text-acero-600">
                  <FilePlus size={16} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold text-navy-950">{t('estimados.crear.blanco_titulo')}</span>
                  <span className="block text-xs leading-snug text-acero-500">{t('estimados.crear.blanco_texto')}</span>
                </span>
                <ArrowRight size={16} className="shrink-0 text-acero-500" />
              </button>

              <div className="flex-1 min-h-0 overflow-y-auto pr-1 custom-scrollbar">
                <div className="grid grid-cols-2 gap-3">
                  {enabledTrades.map(trade => {
                    const active = selectedTrade === trade.id;
                    return (
                      <button
                        key={trade.id}
                        type="button"
                        onClick={() => handleTradeSelect(trade.id)}
                        aria-pressed={active}
                        className={`relative flex flex-col items-center gap-2 p-3.5 rounded-xl border transition-colors duration-150 text-center group active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                          active
                            ? 'border-navy-850 bg-navy-850 text-white'
                            : 'border-acero-200 bg-white text-acero-700 hover:border-acero-300 hover:bg-acero-50'
                        }`}
                      >
                        {active && (
                          <div className="absolute top-2 right-2">
                            <Check size={14} className="text-brand-400" />
                          </div>
                        )}
                        <img
                          src={`/assets/trade-icons/${trade.id}.svg`}
                          alt=""
                          className={`w-7 h-7 transition-opacity duration-150 ${active ? 'brightness-0 invert' : 'opacity-50 group-hover:opacity-80'}`}
                        />
                        <span className={`text-[13px] font-medium ${active ? 'text-white' : 'text-navy-950'}`}>
                          {trade.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-6 shrink-0">
                <Button variant="ghost" onClick={() => setStep('client')} leftIcon={<ArrowLeft size={14} />}>
                  {t('comun.atras')}
                </Button>
                <Button
                  variant="primary"
                  onClick={goToScopeStep}
                  disabled={!selectedTrade}
                  className="flex-1"
                  rightIcon={<ArrowRight size={16} />}
                >
                  {t('estimados.crear.siguiente')}
                </Button>
              </div>
            </div>
          )}

          {step === 'scope' && (
            <div className="animate-in fade-in flex flex-col h-full">
              <div className="mb-5">
                <p className="label-ui mb-1.5">{t('estimados.crear.paso3')}</p>
                <h3 className="text-[22px] leading-tight font-semibold tracking-tight text-navy-950">{t('estimados.crear.que_haces')}</h3>
                <p className="text-sm text-acero-500 mt-1">
                  {t('estimados.crear.ayuda_alcance')}
                </p>
              </div>

              {scopeOptions.length > 6 && (
                <div className="relative shrink-0 mb-4">
                  <Search className="absolute left-3 top-3.5 text-slate-400" size={18} />
                  <input
                    autoFocus
                    type="text"
                    placeholder={t('estimados.crear.buscar_alcance')}
                    className={`${inputClass} pl-10`}
                    value={scopeSearch}
                    onChange={e => setScopeSearch(e.target.value)}
                  />
                </div>
              )}

              <div className="flex-1 min-h-0 overflow-y-auto pr-1 custom-scrollbar">
                <div className="grid grid-cols-1 gap-2">
                  {visibleScopeOptions.map(scope => {
                    const active = selectedScope?.id === scope.id;
                    const { Icon } = scope;
                    return (
                      <button
                        key={scope.id}
                        type="button"
                        // Clearing the box on pick puts the whole list back, so
                        // the choice you just made is visible above the button
                        // instead of hidden behind a stale search term.
                        onClick={() => {
                          setSelectedScope({ id: scope.id, label: scope.label });
                          setScopeSearch('');
                        }}
                        aria-pressed={active}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-colors duration-150 flex items-center gap-3 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
                          active
                            ? 'border-navy-850 bg-navy-850 text-white'
                            : 'border-acero-200 bg-white text-navy-950 hover:border-acero-300 hover:bg-acero-50'
                        }`}
                      >
                        {Icon && (
                          <Icon size={18} className={`shrink-0 ${active ? 'text-white' : 'text-acero-400'}`} />
                        )}
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium text-sm">{scope.label}</span>
                          {scope.note && (
                            <span className={`block text-xs mt-0.5 truncate ${active ? 'text-white/60' : 'text-acero-400'}`}>
                              {scope.note}
                            </span>
                          )}
                        </span>
                        {active && <Check size={16} className="text-brand-400 shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                {visibleScopeOptions.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                    <Search size={32} className="mb-2 opacity-50" />
                    <p className="text-sm font-medium">{t('estimados.crear.sin_alcance')} "{scopeSearch}"</p>
                    <button
                      onClick={() => setScopeSearch('')}
                      className="mt-2 text-xs font-bold text-blue-500 hover:text-blue-600"
                    >
                      {t('estimados.crear.ver_todo')}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-6 shrink-0">
                <Button variant="ghost" onClick={() => setStep('trade')} leftIcon={<ArrowLeft size={14} />}>
                  {t('comun.atras')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleFinish}
                  disabled={!selectedScope}
                  className="flex-1"
                  rightIcon={<ArrowRight size={16} />}
                >
                  {t('estimados.crear.armar_estimado')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateEstimateModal;
